import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Minus, Plus, Search, ShoppingBag, Trash2 } from 'lucide-react';
import { useCart } from '../../lib/cart';
import { money } from '../../lib/format';
import { Empty } from '../../components/ui';
import { useI18n } from '../../i18n';
import { usePublic } from './context';
import { ProductModal } from './ProductModal';

export function MenuPage() {
  const { restaurant: r, menu } = usePublic();
  const { t, demo } = useI18n();
  const { productId } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [tag, setTag] = useState<string | null>(null);
  const cat = params.get('cat') ?? menu.categories[0]?.id;
  const base = `/r/${r.slug}`;
  const cart = useCart();
  const topRef = useRef<HTMLDivElement>(null);

  const searching = q.trim().length > 0;
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // Search matches the text on screen and the stored text, whatever the language.
    return menu.items.filter((i) =>
      needle
        ? `${i.name} ${i.description} ${demo(i.name)} ${demo(i.description)}`.toLowerCase().includes(needle)
        : i.categoryId === cat && (!tag || i.config.tags?.includes(tag)),
    );
  }, [menu.items, q, cat, tag, demo]);
  const tags = useMemo(() => [...new Set(menu.items.filter((i) => i.categoryId === cat).flatMap((i) => i.config.tags ?? []))], [menu.items, cat]);
  const current = menu.categories.find((c) => c.id === cat);
  const product = productId ? menu.items.find((i) => i.id === productId) : undefined;

  useEffect(() => setTag(null), [cat]);

  return (
    <main className="pub-main">
      <div className="menu-layout">
        <aside className="menu-cats" aria-label={t.menu.categories}>
          {menu.categories.map((c) => (
            <button
              key={c.id}
              className={!searching && c.id === cat ? 'active' : ''}
              onClick={() => {
                setQ('');
                setParams({ cat: c.id });
                topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {demo(c.name)}
            </button>
          ))}
        </aside>

        <section className="stack-lg" ref={topRef}>
          <div className="search">
            <Search size={18} />
            <input className="input" placeholder={t.menu.searchPlaceholder} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t.menu.search} data-testid="menu-search" />
          </div>
          <div>
            <h1 className="section-title">{searching ? t.menu.resultsFor(q) : t.menu.ourCategory(current ? demo(current.name) : undefined)}</h1>
            {!searching && <p className="muted">{t.menu.intro}</p>}
          </div>
          {!searching && tags.length > 0 && (
            <div className="row wrap">
              <button className={`chip ${!tag ? 'active' : ''}`} onClick={() => setTag(null)}>
                {t.menu.allTags}
              </button>
              {tags.map((tg) => (
                <button key={tg} className={`chip ${tag === tg ? 'active' : ''}`} onClick={() => setTag(tg)}>
                  {demo(tg)}
                </button>
              ))}
            </div>
          )}
          {items.length === 0 ? (
            <Empty icon={<Search size={36} />}>{t.menu.noMatch}</Empty>
          ) : (
            <div className="product-grid" data-testid="product-grid">
              {items.map((i) => (
                <article key={i.id} className="product-card" onClick={() => nav(`${base}/product/${i.id}${cat ? `?cat=${cat}` : ''}`)} data-testid="product-card">
                  <div className="pimg">
                    <img src={i.imageUrl ?? ''} alt={demo(i.name)} loading="lazy" />
                  </div>
                  <div className="pbody">
                    <h3>{demo(i.name)}</h3>
                    <p className="desc">{demo(i.description)}</p>
                    <span className="price">
                      {i.config.groups.some((g) => g.required && g.options.some((o) => o.priceDeltaCents > 0)) ? t.menu.fromPrefix : ''}
                      {money(i.startingPriceCents)}
                    </span>
                  </div>
                  <div className="pfoot">
                    <button className="btn btn-primary btn-block btn-sm" aria-label={t.menu.addItem(demo(i.name))}>
                      <Plus size={16} /> {t.menu.add}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <CartPanel />
      </div>

      {cart.count > 0 && (
        <div className="sticky-cta">
          <Link to={`${base}/cart`} className="btn btn-primary btn-lg btn-block" data-testid="sticky-cart">
            <ShoppingBag size={18} /> {t.menu.stickyCart(cart.count, money(cart.subtotalCents))}
          </Link>
        </div>
      )}

      {product && <ProductModal item={product} onClose={() => nav(`${base}/menu${cat ? `?cat=${cat}` : ''}`)} />}
    </main>
  );
}

export function CartPanel() {
  const { restaurant: r } = usePublic();
  const { t } = useI18n();
  const cart = useCart();
  const nav = useNavigate();
  const fee = cart.mode === 'DELIVERY' ? r.deliveryFeeCents : 0;
  return (
    <aside className="card card-pad cart-panel stack" aria-label={t.menu.yourOrder}>
      <div className="row-between">
        <h3>{t.menu.yourOrderCount(cart.count)}</h3>
        {cart.count > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={cart.clear} style={{ color: 'var(--red)' }}>
            {t.menu.clearAll}
          </button>
        )}
      </div>
      {cart.lines.length === 0 ? (
        <p className="muted small">{t.menu.emptyCart}</p>
      ) : (
        <div>
          {cart.lines.map((l) => (
            <CartLineRow key={l.key} line={l} />
          ))}
        </div>
      )}
      <div className="totals">
        <div className="row-between">
          <span className="muted">{t.common.subtotal}</span>
          <span className="money">{money(cart.subtotalCents)}</span>
        </div>
        {cart.mode === 'DELIVERY' && (
          <div className="row-between">
            <span className="muted">{t.common.deliveryFee}</span>
            <span className="money">{money(fee)}</span>
          </div>
        )}
        <div className="row-between total">
          <span>{t.menu.estimatedTotal}</span>
          <span className="money">{money(cart.subtotalCents + fee)}</span>
        </div>
      </div>
      <button className="btn btn-primary btn-lg btn-block" disabled={cart.count === 0} onClick={() => nav(`/r/${r.slug}/checkout`)}>
        {t.menu.checkout}
      </button>
    </aside>
  );
}

/** Options of a cart line in the current language (older carts only have the joined label). */
export function useLineOptions() {
  const { demo } = useI18n();
  return (line: ReturnType<typeof useCart>['lines'][number]) =>
    line.optionNames ? line.optionNames.map(demo).join(', ') : line.optionsLabel.split(', ').map(demo).join(', ');
}

export function CartLineRow({ line }: { line: ReturnType<typeof useCart>['lines'][number] }) {
  const cart = useCart();
  const { t, demo } = useI18n();
  const options = useLineOptions()(line);
  return (
    <div className="cart-line" data-testid="cart-line">
      <img src={line.imageUrl ?? ''} alt="" />
      <div className="stack" style={{ gap: 6 }}>
        <div>
          <b>{demo(line.name)}</b>
          {options && <div className="muted small">{options}</div>}
        </div>
        <div className="row">
          <div className="qty">
            <button onClick={() => cart.setQty(line.key, line.quantity - 1)} aria-label={t.menu.removeOne}>
              {line.quantity === 1 ? <Trash2 size={15} /> : <Minus size={15} />}
            </button>
            <span>{line.quantity}</span>
            <button onClick={() => cart.setQty(line.key, line.quantity + 1)} aria-label={t.menu.addOne}>
              <Plus size={15} />
            </button>
          </div>
        </div>
      </div>
      <b className="money">{money(line.unitPriceCents * line.quantity)}</b>
    </div>
  );
}
